import { ChevronDown, ChevronUp, Copy, Edit3, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { TAGS } from '../constants';
import type { Reminder } from '../types';
import { countdownText, isClosed, isExpired, repeatLabel } from '../utils';
import { EmptyState } from './EmptyState';

interface Props {
  reminders: Reminder[];
  filter: string;
  search: string;
  onEdit: (reminder: Reminder) => void;
  onToggle: (reminder: Reminder) => void;
  onCopy: (reminder: Reminder) => void;
  onDelete: (reminder: Reminder) => void;
}

const groupKey = (item: Reminder) => `${item.title}|${item.tag}|${item.repeatType}`;

export function ReminderView(props: Props) {
  const search = props.search.trim().toLocaleLowerCase();
  const filtered = props.reminders.filter((item) => {
    if (search && !item.title.toLocaleLowerCase().includes(search)) return false;
    if (props.filter === 'expired') return isExpired(item);
    if (props.filter === 'closed') return isClosed(item);
    if (props.filter !== 'all') return item.tag === props.filter && !isExpired(item) && !isClosed(item);
    return !isExpired(item) && !isClosed(item);
  });

  if (!filtered.length) {
    return <EmptyState
      title={search || props.filter !== 'all' ? '找不到符合的提醒' : '還沒有提醒'}
      detail={!search && props.filter === 'all' && !props.reminders.length ? '點右下角 + 新增一個吧' : undefined}
    />;
  }

  return (
    <section className="item-list reminder-list" aria-label="提醒清單">
      {TAGS.map((tag) => {
        const tagged = filtered.filter((item) => item.tag === tag.value);
        if (!tagged.length) return null;
        const groups = Object.values(tagged.reduce<Record<string, Reminder[]>>((result, item) => {
          const key = groupKey(item);
          (result[key] ||= []).push(item);
          return result;
        }, {}));
        return (
          <div className="tag-section" key={tag.value}>
            <header className="tag-heading">
              <span style={{ backgroundColor: tag.color }} />
              <strong style={{ color: tag.color }}>{tag.label}</strong>
              <small>{tagged.length} 項</small>
            </header>
            {groups.map((group) => (
              group.length === 1
                ? <ReminderCard key={group[0].id} item={group[0]} {...props} />
                : <MergedReminderCard key={`${groupKey(group[0])}-${group.map((item) => item.id).join('-')}`} items={group} {...props} />
            ))}
          </div>
        );
      })}
    </section>
  );
}

function ReminderCard({ item, onEdit, onToggle, onCopy, onDelete }: { item: Reminder } & Pick<Props, 'onEdit' | 'onToggle' | 'onCopy' | 'onDelete'>) {
  const tag = TAGS.find((candidate) => candidate.value === item.tag) ?? TAGS[0];
  const expired = isExpired(item);
  const dimmed = expired || !item.isEnabled;
  return (
    <article className={`item-card reminder-card ${dimmed ? 'dimmed' : ''}`}>
      <span className="card-accent" style={{ backgroundColor: tag.color }} />
      <button className="card-main reminder-main" type="button" onClick={() => onEdit(item)}>
        <span className="reminder-title-line">
          {expired && <span className="status-badge error">已過期</span>}
          {!expired && !item.isEnabled && <span className="status-badge">已關閉</span>}
          <strong className={expired ? 'struck' : ''}>{item.title}</strong>
        </span>
        <span className="reminder-time-row">
          <b>{String(item.hour).padStart(2, '0')}:{String(item.minute).padStart(2, '0')}</b>
          {item.repeatType === 'none' && <span>{item.year}/{String(item.month).padStart(2, '0')}/{String(item.day).padStart(2, '0')}</span>}
        </span>
        <span className="card-meta">
          <span style={{ color: tag.color }}>{tag.label}</span>
          {repeatLabel(item.repeatType) && <span>· {repeatLabel(item.repeatType)}</span>}
          {item.advanceMinutes > 0 && <span className="tertiary">· 提前{item.advanceMinutes}分鐘</span>}
          {item.isEnabled && !expired && <span>{countdownText(item)}</span>}
        </span>
      </button>
      <ReminderControls item={item} onToggle={onToggle} onEdit={onEdit} onCopy={onCopy} onDelete={onDelete} />
    </article>
  );
}

function MergedReminderCard({ items, onEdit, onToggle, onCopy, onDelete }: { items: Reminder[] } & Pick<Props, 'onEdit' | 'onToggle' | 'onCopy' | 'onDelete'>) {
  const [expanded, setExpanded] = useState(false);
  const first = items[0];
  const tag = TAGS.find((candidate) => candidate.value === first.tag) ?? TAGS[0];
  return (
    <article className="item-card merged-card">
      <span className="card-accent" style={{ backgroundColor: tag.color }} />
      <div className="merged-content">
        <button className="merged-heading" type="button" onClick={() => setExpanded((value) => !value)}>
          <strong>{first.title}</strong>
          <span>{items.length} 個時段</span>
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
        <div className="time-chips">
          {items.map((item) => (
            <button type="button" key={item.id} className={isExpired(item) ? 'expired' : ''} onClick={() => onEdit(item)}>
              {String(item.hour).padStart(2, '0')}:{String(item.minute).padStart(2, '0')}
            </button>
          ))}
        </div>
        <span className="card-meta">
          <span style={{ color: tag.color }}>{tag.label}</span>
          {repeatLabel(first.repeatType) && <span>· {repeatLabel(first.repeatType)}</span>}
          {first.advanceMinutes > 0 && <span className="tertiary">· 提前{first.advanceMinutes}分鐘</span>}
        </span>
        {expanded && (
          <div className="merged-rows">
            {items.map((item) => (
              <div key={item.id}>
                <b>{String(item.hour).padStart(2, '0')}:{String(item.minute).padStart(2, '0')}</b>
                {item.repeatType === 'none' && <small>{item.year}/{String(item.month).padStart(2, '0')}/{String(item.day).padStart(2, '0')}</small>}
                <ReminderControls item={item} onToggle={onToggle} onEdit={onEdit} onCopy={onCopy} onDelete={onDelete} compact />
              </div>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

function ReminderControls({ item, onToggle, onEdit, onCopy, onDelete, compact = false }: {
  item: Reminder;
  compact?: boolean;
} & Pick<Props, 'onToggle' | 'onEdit' | 'onCopy' | 'onDelete'>) {
  return (
    <div className={`card-controls reminder-controls ${compact ? 'inline' : ''}`}>
      <label className="switch" aria-label={item.isEnabled ? '關閉提醒' : '啟用提醒'}>
        <input type="checkbox" checked={item.isEnabled} onChange={() => onToggle(item)} />
        <span />
      </label>
      <div className="mini-actions">
        <button type="button" onClick={() => onCopy(item)} aria-label="複製"><Copy size={compact ? 14 : 16} /></button>
        <button type="button" onClick={() => onEdit(item)} aria-label="編輯"><Edit3 size={compact ? 14 : 16} /></button>
        <button className="danger-icon" type="button" onClick={() => onDelete(item)} aria-label="刪除"><Trash2 size={compact ? 14 : 16} /></button>
      </div>
    </div>
  );
}
